<template>
  <div>

    <add-to-calendar-dialog v-if="showAddToCalendar" :event="order.event" v-model="showAddToCalendar" />

    <template v-if="showHeaders">
      <div class="text--label-active mb-4">
        <div>
          <span class="overline short grey--text mr-1">From</span>
          <template v-if="from">{{from}} &lt;</template>
          <span v-html="skin.email_from || 'delivery@ellacard.com'" />
          <template v-if="from">&gt;</template>
        </div>
        <div v-if="!skin.email_from && global.profile">
          <span class="overline short grey--text mr-1">Reply-to</span>
          {{global.profile.email}}
        </div>
        <div>
          <span class="overline short grey--text mr-1">Subject</span>
          {{skin.email_subject}}
        </div>
      </div>
    </template>

    <div>
      <!-- Note that this is NOT identical to the actual email that gets set out - links have been disabled and styles changed to display neatly in context (mainly avoiding conflicts with global CSS classes) -->
      <div class="email-root">
        <v-card
          tile
          v-bind:style="{ color, 'background-color': skin.page_background_color || '#FFF' }"
        >
          <div class="pa-4">
            <p class="d-flex align-center">
              <span class="mx-auto email-subject preserve-newlines" v-html="skin.email_subject" />
            </p>
            <p v-if="toTxt"><span class="email-to-from">TO</span> <span v-text="toTxt" /></p>
            <p v-if="fromTxt"><span class="email-to-from">FROM</span> <span v-text="fromTxt" /></p>
            <template v-if="followUpMsg">
              <br>
              <p class="preserve-newlines">{{followUpMsg}}</p>
            </template>
          </div>

          <v-card
            class="mx-auto relative overflow-hidden"
            style="max-width: 92%; aspect-ratio: 7 / 5;"
            hover tile
            @click="$emit('preview')"
          >
            <envelope style="position: absolute; inset: -20% 0 -20% 0;" :skin="skin" :from="from" :to="to" />
          </v-card>

          <div class="px-4 py-8">
            <v-btn color="primary" @click="$emit('preview')">
              Open {{order.type == 'INVITATION' ? 'invitation & RSVP' : 'card'}}
            </v-btn>
          </div>

          <div v-if="order.type == 'INVITATION' && order.event" class="pb-8 text-center">
            <template v-if="order.event.name">
              <br>
              <div class="font-weight-bold preserve-newlines">{{order.event.name}}</div>
              <br v-if="!order.event.lz_date">
            </template>
            <template v-if="order.event.lz_date">
              <br>
              <div class="font-weight-bold">{{order.event.lz_date}}</div>
              <div>{{order.event.lz_time_range}} {{order.event.lz_tz_name}}</div>
              <br v-if="!hasLocation">
            </template>
            <template v-if="hasLocation">
              <br>
              <div v-if="order.event.location" class="font-weight-bold preserve-newlines">{{order.event.location}}</div>
              <div v-if="order.event.address" class="preserve-newlines">{{order.event.address}}</div>
              <br>
            </template>
            <div v-if="order.event.lz_date">
              <br>
              <v-btn color="primary" @click="showAddToCalendar = true">Add to calendar</v-btn>
            </div>
            <div v-if="order.event.url_map">
              <br>
              <v-btn color="primary" target="_blank" :href="order.event.url_map">Map link</v-btn>
            </div>
            <div v-if="order.event.url_virtual">
              <br>
              <v-btn color="primary" target="_blank" :href="order.event.url_virtual">Virtual event link</v-btn>
            </div>
          </div>
        </v-card>
      </div>


      <div class="px-4 py-8 d-flex justify-center align-center">
        <!-- The 'email-logo' class is used externally - keep it around -->
        <div class="email-logo relative">
          <a v-if="skin.email_footer_logo" target="_blank" :href="skin.email_footer_url">
            <img class="d-block" height="67" :src="skin.email_footer_logo">
          </a>
        </div>
      </div>

      <div class="email-secondary text-center pb-4 px-4">
        <template v-if="email">This message was sent to: {{email}}<br></template>
        <a class="black--text" href="javascript:void(0)">Unsubscribe</a>
      </div>
    </div>

  </div>
</template>

<style scoped>
.email-root {
  max-width: 600px;
  margin: 0 auto;
  font-family: sans-serif;
  font-size: 14px;
  text-align: center;
}
.email-subject {
  font-size: 24px;
  font-weight: bold;
}
.email-to-from {
  font-size: 10px;
  letter-spacing: 1px;
  opacity: .5;
  margin-right: 4px;
}
.email-secondary {
  font-size: 10px;
}
</style>

<script>
import AddToCalendarDialog from '@/components/AddToCalendarDialog.vue';
import ContentEditableDiv from '@/components/ContentEditableDiv.vue';
import Envelope from '@/components/Envelope.vue';
import { getBrightness } from '@/utils/group-video-template.js';
import stripHTML from '@/utils/strip-html.js';

export default {
  name: 'recipient-preview-email',

  components: {
    AddToCalendarDialog,
    ContentEditableDiv,
    Envelope
  },

  props: {
    orderId: String, // Purely cosmetic, could be set to a placeholder
    order: Object,
    from: String,
    to: String,
    email: String,
    followUpMsg: String,
    showHeaders: Boolean
  },

  data() {
    return {
      showAddToCalendar: false
    };
  },

  computed: {
    skin() {
      return this.order.skin;
    },

    fromTxt() {
      return stripHTML(this.from);
    },

    toTxt() {
      return stripHTML(this.to);
    },

    hasLocation() {
      const e = this.order.event;
      return e && (e.location || e.url || (!e.is_virtual && e.address));
    },

    isDarkBackground() {
      return getBrightness(this.skin.page_background_color || '#FFF') < 160;
    },

    color() {
      return this.isDarkBackground ? '#F5F5F5' : '#344558';
    }
  }
}
</script>
